import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import {
  CalculoRevisionalData,
  CalculoRevisionalResult,
  AtualizacaoValoresData,
  AtualizacaoValoresResult,
  CalculoTrabalhistaData,
  CalculoTrabalhistaResult,
  CalculoFGTSData,
  CalculoFGTSResult,
  PensaoAlimenticiaData,
  PensaoAlimenticiaResult,
  CalculoAluguelData,
  CalculoAluguelResult,
  SuperendividamentoData,
  SuperendividamentoResult,
  DosimetriaPenaData,
  DosimetriaPenaResult,
  ProgressaoRegimeData,
  ProgressaoRegimeResult,
  CalculoRMCData,
  CalculoRMCResult,
  CalculoINSSData,
  CalculoINSSResult,
  CalculoDivorcioData,
  CalculoDivorcioResult
} from "@/types/calculos";
import indicesData from "@/data/indices_economicos.json";

export const useCalculos = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const calcularPrestacao = (pv: number, taxa: number, parcelas: number): number => {
    if (taxa === 0) return pv / parcelas;
    return (pv * taxa) / (1 - Math.pow(1 + taxa, -parcelas));
  };

  const calcularRevisional = async (dados: CalculoRevisionalData): Promise<CalculoRevisionalResult> => {
    setLoading(true);
    try {
      const { valorFinanciado, taxaContratual, taxaLegal, numParcelas } = dados;
      
      const prestacaoOriginal = calcularPrestacao(valorFinanciado, taxaContratual / 100, numParcelas);
      const prestacaoCorrigida = calcularPrestacao(valorFinanciado, taxaLegal / 100, numParcelas);
      const diferenca = prestacaoOriginal - prestacaoCorrigida;
      const economia = diferenca * numParcelas;

      toast({
        title: "Cálculo realizado com sucesso",
        description: "Cálculo revisional processado."
      });

      return {
        prestacaoOriginal,
        prestacaoCorrigida,
        diferenca,
        economia
      };
    } catch (error) {
      toast({
        title: "Erro no cálculo",
        description: "Verifique os dados informados.",
        variant: "destructive"
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const calcularAtualizacaoValores = async (dados: AtualizacaoValoresData): Promise<AtualizacaoValoresResult> => {
    setLoading(true);
    try {
      const { valorOriginal, dataInicial, dataFinal, indice, taxaJuros = 0 } = dados;
      
      // Buscar índices do período
      const inicioAno = new Date(dataInicial).getFullYear();
      const fimAno = new Date(dataFinal).getFullYear();
      
      let fatorCorrecao = 1;
      for (let ano = inicioAno; ano <= fimAno; ano++) {
        for (let mes = 1; mes <= 12; mes++) {
          const chave = `${ano}-${mes.toString().padStart(2, '0')}`;
          const valorIndice = indicesData[indice][chave as keyof typeof indicesData.ipca];
          if (valorIndice) {
            fatorCorrecao *= (1 + valorIndice / 100);
          }
        }
      }

      const correcaoMonetaria = valorOriginal * (fatorCorrecao - 1);
      const juros = valorOriginal * (taxaJuros / 100);
      const valorAtualizado = valorOriginal * fatorCorrecao;
      const valorTotal = valorAtualizado + juros;

      return {
        valorAtualizado,
        correcaoMonetaria,
        juros,
        valorTotal
      };
    } finally {
      setLoading(false);
    }
  };

  const calcularTrabalhista = async (dados: CalculoTrabalhistaData): Promise<CalculoTrabalhistaResult> => {
    setLoading(true);
    try {
      const { salario, diasTrabalhados, mesesTrabalhados, temFerias, tem13 } = dados;
      
      const saldoSalario = (salario / 30) * diasTrabalhados;
      const ferias = temFerias ? (salario / 12) * mesesTrabalhados * 1.33 : 0;
      const decimoTerceiro = tem13 ? (salario / 12) * mesesTrabalhados : 0;
      const saldoFGTS = salario * mesesTrabalhados * 0.08;
      const multaFGTS = saldoFGTS * 0.40;
      const total = saldoSalario + ferias + decimoTerceiro + multaFGTS;

      return {
        saldoSalario,
        ferias,
        decimoTerceiro,
        multaFGTS,
        total
      };
    } finally {
      setLoading(false);
    }
  };

  const calcularFGTS = async (dados: CalculoFGTSData): Promise<CalculoFGTSResult> => {
    setLoading(true);
    try {
      const { saldoOriginal, dataInicial, dataFinal } = dados;
      
      // Aplicar correção INPC vs TR
      const correcaoINPC = await calcularAtualizacaoValores({
        valorOriginal: saldoOriginal,
        dataInicial,
        dataFinal,
        indice: 'inpc'
      });

      const correcaoTR = await calcularAtualizacaoValores({
        valorOriginal: saldoOriginal,
        dataInicial,
        dataFinal,
        indice: 'tr'
      });

      const saldoCorrigido = correcaoINPC.valorAtualizado;
      const diferenca = saldoCorrigido - correcaoTR.valorAtualizado;

      return {
        saldoCorrigido,
        diferenca
      };
    } finally {
      setLoading(false);
    }
  };

  const calcularPensaoAlimenticia = async (dados: PensaoAlimenticiaData): Promise<PensaoAlimenticiaResult> => {
    setLoading(true);
    try {
      const { salarioLiquido, percentual } = dados;
      const valorPensao = salarioLiquido * (percentual / 100);

      return { valorPensao };
    } finally {
      setLoading(false);
    }
  };

  const calcularAluguel = async (dados: CalculoAluguelData): Promise<CalculoAluguelResult> => {
    setLoading(true);
    try {
      const { valorMensal, diasAtraso, multaPercentual, jurosDiarios } = dados;
      
      const multa = valorMensal * (multaPercentual / 100);
      const juros = valorMensal * (jurosDiarios / 100) * diasAtraso;
      const total = valorMensal + multa + juros;

      return {
        valorDevido: valorMensal,
        multa,
        juros,
        total
      };
    } finally {
      setLoading(false);
    }
  };

  const calcularSuperendividamento = async (dados: SuperendividamentoData): Promise<SuperendividamentoResult> => {
    setLoading(true);
    try {
      const { rendaLiquida, gastosMensais } = dados;
      
      const capacidadePagamento = rendaLiquida - gastosMensais;
      const parcelaMaxima = capacidadePagamento * 0.30;

      return {
        parcelaMaxima,
        capacidadePagamento
      };
    } finally {
      setLoading(false);
    }
  };

  const calcularDosimetriaPena = async (dados: DosimetriaPenaData): Promise<DosimetriaPenaResult> => {
    setLoading(true);
    try {
      const {
        penaMinima,
        penaMaxima,
        antecedentes,
        condutaSocial,
        personalidade,
        motivos,
        circunstancias,
        consequencias,
        comportamento
      } = dados;

      const fatores = [antecedentes, condutaSocial, personalidade, motivos, circunstancias, consequencias, comportamento];
      const somaFatores = fatores.reduce((acc, curr) => acc + curr, 0);
      const aumentoPercentual = (somaFatores / 7) * 0.10; // Até 10% de aumento

      const penaBase = penaMinima + ((penaMaxima - penaMinima) * aumentoPercentual);
      const penaFinal = Math.min(penaBase, penaMaxima);

      return {
        penaBase,
        penaFinal,
        aumentoPercentual: aumentoPercentual * 100
      };
    } finally {
      setLoading(false);
    }
  };

  const calcularProgressaoRegime = async (dados: ProgressaoRegimeData): Promise<ProgressaoRegimeResult> => {
    setLoading(true);
    try {
      const { penaTotal, crimeHediondo, reincidente } = dados;
      
      let percentual = 1/6; // 16.67% - réu primário
      if (crimeHediondo && !reincidente) percentual = 2/5; // 40%
      if (crimeHediondo && reincidente) percentual = 3/5; // 60%

      const tempoNecessario = penaTotal * percentual;
      const percentualCumprido = percentual * 100;
      
      const hoje = new Date();
      const dataProgressao = new Date(hoje.getTime() + (tempoNecessario * 365 * 24 * 60 * 60 * 1000));

      return {
        tempoNecessario,
        percentualCumprido,
        dataProgressao: dataProgressao.toLocaleDateString()
      };
    } finally {
      setLoading(false);
    }
  };

  const calcularRMC = async (dados: CalculoRMCData): Promise<CalculoRMCResult> => {
    setLoading(true);
    try {
      const { salarioLiquido, margem } = dados;
      
      const valorDisponivelConsignado = salarioLiquido * (margem / 100);
      const parcelaMaxima = valorDisponivelConsignado * 0.05; // 5% da margem

      return {
        valorDisponivelConsignado,
        parcelaMaxima
      };
    } finally {
      setLoading(false);
    }
  };

  const calcularINSS = async (dados: CalculoINSSData): Promise<CalculoINSSResult> => {
    setLoading(true);
    try {
      const { valorPago, dataInicial, dataFinal } = dados;
      
      const correcaoIPCA = await calcularAtualizacaoValores({
        valorOriginal: valorPago,
        dataInicial,
        dataFinal,
        indice: 'ipca'
      });

      return {
        valorCorrigido: correcaoIPCA.valorAtualizado,
        diferenca: correcaoIPCA.correcaoMonetaria
      };
    } finally {
      setLoading(false);
    }
  };

  const calcularDivorcio = async (dados: CalculoDivorcioData): Promise<CalculoDivorcioResult> => {
    setLoading(true);
    try {
      const { patrimonioTotal, regimeBens } = dados;
      
      let divisao = 0.5; // 50% para cada um por padrão
      
      if (regimeBens === 'separacao_bens') {
        divisao = 0; // Não há partilha
      }

      const quinhaoConjuge1 = patrimonioTotal * divisao;
      const quinhaoConjuge2 = patrimonioTotal * divisao;

      return {
        quinhaoConjuge1,
        quinhaoConjuge2
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    calcularRevisional,
    calcularAtualizacaoValores,
    calcularTrabalhista,
    calcularFGTS,
    calcularPensaoAlimenticia,
    calcularAluguel,
    calcularSuperendividamento,
    calcularDosimetriaPena,
    calcularProgressaoRegime,
    calcularRMC,
    calcularINSS,
    calcularDivorcio
  };
};